/**
 * AgentProgress — live step-by-step timeline shown while the SQL agent works.
 *
 * Receives an array of steps (accumulated from "thought" SSE frames) and a
 * `streaming` flag.  When streaming stops, collapses to a one-line summary
 * after a short delay.
 *
 * Step shape: { step, label, detail, status: "active" | "done" }
 *
 * Visual language:
 *   ✓  done step    — muted check mark, greyed out label
 *   ●  active step  — pulsing terracotta dot, full ink label
 *
 * Matches the editorial aesthetic: no card backgrounds, just a clean indented
 * list with a leading eyebrow label.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, ChevronDown, ChevronUp, Database } from "lucide-react";

const STEP_ICONS = {
  start:       Database,
  query:       Database,
  schema:      Database,
  list_tables: Database,
  tool:        Loader2,
};

export default function AgentProgress({ steps = [], streaming }) {
  const [collapsed, setCollapsed]     = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  // When streaming stops, wait 800ms then collapse to summary line.
  useEffect(() => {
    if (!streaming && steps.length > 0) {
      const t = setTimeout(() => setShowSummary(true), 800);
      return () => clearTimeout(t);
    }
    // Reset when a new query starts
    setShowSummary(false);
    setCollapsed(false);
  }, [streaming, steps.length]);

  if (steps.length === 0) return null;

  const queryCount  = steps.filter((s) => s.step === "query").length;
  const schemaCount = steps.filter((s) => s.step === "schema").length;

  // ── Collapsed summary line (after streaming finishes) ─────────────────
  if (showSummary && !collapsed) {
    const parts = [];
    if (steps.length > 0) parts.push(`${steps.length} step${steps.length !== 1 ? "s" : ""}`);
    if (queryCount > 0)   parts.push(`${queryCount} quer${queryCount !== 1 ? "ies" : "y"}`);
    if (schemaCount > 0)  parts.push(`${schemaCount} schema${schemaCount !== 1 ? "s" : ""} inspected`);

    return (
      <div className="agent-progress-summary">
        <CheckCircle2 className="w-3 h-3 text-[var(--color-status-even)]" />
        <span>{parts.join(" · ")}</span>
        <button
          onClick={() => setCollapsed(false)}
          className="agent-progress-toggle"
          aria-label="Show steps"
        >
          <ChevronDown className="w-3 h-3" />
          details
        </button>
      </div>
    );
  }

  // ── Expanded step timeline ─────────────────────────────────────────────
  return (
    <div className="agent-progress">
      <div className="agent-progress-header">
        <span className="eyebrow !text-[var(--color-ink-3)]" style={{ fontSize: "9px" }}>
          AGENT PROGRESS
        </span>
        {!streaming && (
          <button
            onClick={() => setShowSummary(true)}
            className="agent-progress-toggle"
            aria-label="Collapse steps"
          >
            <ChevronUp className="w-3 h-3" />
            collapse
          </button>
        )}
      </div>

      <ol className="agent-steps">
        {steps.map((step, i) => (
          <StepRow key={i} step={step} isLast={i === steps.length - 1} streaming={streaming} />
        ))}
        {/* Animated tail while agent is still working and last step is "done" */}
        {streaming && steps.every((s) => s.status === "done") && (
          <li className="agent-step agent-step-waiting">
            <span className="agent-step-dot agent-step-dot-pulse" />
            <span className="agent-step-label">Thinking…</span>
          </li>
        )}
      </ol>
    </div>
  );
}

function StepRow({ step, isLast, streaming }) {
  const isActive = step.status === "active" && streaming;
  const isDone   = step.status === "done" || (step.status === "active" && !streaming);

  return (
    <li className={`agent-step ${isDone ? "agent-step-done" : ""} ${isActive ? "agent-step-active" : ""}`}>
      {/* Status indicator */}
      <span className="agent-step-indicator">
        {isDone ? (
          <CheckCircle2 className="w-3 h-3 text-[var(--color-status-even)]" />
        ) : (
          <span className="agent-step-dot agent-step-dot-pulse" />
        )}
      </span>

      {/* Content */}
      <span className="agent-step-body">
        <span className="agent-step-label">{step.label}</span>
        {step.detail && (
          <span className="agent-step-detail mono">{formatDetail(step.detail)}</span>
        )}
      </span>
    </li>
  );
}

/** Truncate long SQL or table lists to keep the UI clean. */
function formatDetail(detail) {
  if (!detail) return "";
  // SQL query — show just the first line
  if (detail.includes("\n")) {
    const first = detail.split("\n")[0].trim();
    return first.length > 80 ? first.slice(0, 80) + "…" : first;
  }
  return detail.length > 80 ? detail.slice(0, 80) + "…" : detail;
}
